import { makeAutoObservable, runInAction } from 'mobx';
import type { ShiritoriGameDb } from '@/firebase/shiritori-db';
import { Player, type PlayerTeam } from './player';
import { getDeviceId } from '@/util/device-id';
import { type GameConfigDto, type GameDto, GameState, type TurnTimerStateDto } from './game';
import { getShiritoriEnding, normalizeToHiragana } from './kana';
import type { ShiritoriWordDto } from './shiritori-word';
import type { TurnDto } from './turn';

interface IsPlayableResult {
    ok: boolean,
    messageKey?: 'syllableMismatch' | 'missingLastWord' | 'wordNotInList';
}

export class ShiritoriGameStore {
    private _db: ShiritoriGameDb;
    private _rawData: GameDto = {} as GameDto;

    public deviceId: string = getDeviceId();

    public teams: Record<string, Player[]> = {};
    public turnSortedPlayers: Player[] = [];

    public enemyTeams: PlayerTeam[] = [];
    public playerMap: Record<string, Player> = {};

    public state: GameState = GameState.Loading;

    constructor(db: ShiritoriGameDb) {
        this._db = db;

        this._db.subscribe(this.onDataChanged.bind(this));
        makeAutoObservable(this);
    }

    public get data() {
        return this._rawData;
    }

    public get config() {
        return this._rawData?.config || {} as GameConfigDto;
    }

    public get devicePlayer(): Player {
        return this.playerMap[this.deviceId]!;
    }

    public get gameId() {
        return this._db.gameId;
    }

    public get hasData() {
        return Object.keys(this._rawData || {}).length > 0;
    }

    public get isGameOver() {
        return this.state === GameState.Ended && (!!this._rawData.game?.endState || this.hasForfeit);
    }

    public get hasForfeit() {
        return Object.values(this._rawData.state || {}).includes('forfeit');
    }

    public get turns() {
        return this._rawData.game?.turns || {};
    }

    public get timerConfig() {
        return this.config?.turnTimer;
    }

    public get areHintsAllowed() {
        return this.config.allowHints !== false;
    }

    public get isTimerActive() {
        return this.timerConfig?.mode === 'timer' && (this.timerConfig.seconds || 0) > 0;
    }

    public get turnTimerSeconds() {
        return this.isTimerActive ? (this.timerConfig?.seconds || 0) : 0;
    }

    public get hasDuplicatePlay(): boolean {
        const seen = new Set<string>();

        for (const turn of this.turnValues) {
            const wordId = turn.word?.wordId;
            if (wordId) {
                if (seen.has(wordId)) {
                    return true;
                }
                seen.add(wordId);
            }
        }

        return false;
    }


    public get timerState(): TurnTimerStateDto | undefined {
        const timer = this._rawData.game?.turnTimer || undefined;

        if (!timer || !this.isTimerActive) {
            return undefined;
        }

        if (timer.turnIndex !== this.currentTurnIndex) {
            return undefined;
        }

        return timer;
    }

    public get turnTimerKey() {
        const timer = this.timerState;
        if (!timer) {
            return 'none';
        }

        return `${timer.turnIndex}-${timer.startedAt}-${timer.durationSeconds}`;
    }

    public get hasActiveTimer() {
        return !!this.timerState;
    }

    public currentTurnRemainingSeconds(now = Date.now()) {
        const timer = this.timerState;
        if (!timer) {
            return undefined;
        }

        const deadline = timer.startedAt + timer.durationSeconds * 1000;
        const remaining = Math.ceil((deadline - now) / 1000);
        return Math.max(0, remaining);
    }

    private get turnValues(): TurnDto[] {
        return Object.values(this.turns).filter(Boolean);
    }

    private get totalCells(): number {
        const size = this.config.boardSize || 0;
        return size * size;
    }

    public get isBoardFilled(): boolean {
        return this.turnCount >= this.totalCells;
    }

    /** number of turns have been played so far */
    get turnCount(): number {
        return this.turnValues.filter(Boolean).length;
    }

    get lastTurnIndex(): number {
        if (this.turnCount === 0) {
            return -1;
        }

        return this.turnToSnakeCell(this.turnCount - 1, this.config.boardSize);
    }

    get currentTurnIndex(): number {
        return this.turnToSnakeCell(this.turnCount, this.config.boardSize);
    }

    public get lastPlay(): TurnDto | undefined {
        if (this.turnCount === 0) {
            return undefined;
        }

        for (let orderIndex = this.turnCount - 1; orderIndex >= 0; orderIndex--) {
            const cellIndex = this.turnToSnakeCell(orderIndex, this.config.boardSize);
            const turn = this.turns[cellIndex];
            if (turn?.word) {
                return turn;
            }
        }

        return undefined;
    }

    public get isDeviceTurn() {
        return this.nextToPlay().id === this.deviceId;
    }

    public nextToPlay(): Player {

        if (this.turnSortedPlayers.length === 0) {
            return this.devicePlayer;
        }

        const teamsInOrder = this.orderedTeams;

        // If we don't have teams for some reason, just cycle through players directly
        if (teamsInOrder.length === 0) {
            const playerIndex = this.turnCount % this.turnSortedPlayers.length;
            return this.turnSortedPlayers[playerIndex]!;
        }

        const teamIndex = this.turnCount % teamsInOrder.length;
        const teamToPlay = teamsInOrder[teamIndex]!;
        const playersOnTeam = this.teams[teamToPlay] || [];
        const turnsTakenByTeam = this.turnValues.filter((turn) => {
            const turnTeam = turn.team || this.playerMap[turn.playerId]?.team;
            return turnTeam === teamToPlay;
        }).length;

        const playerIndex = playersOnTeam.length === 0 ? 0 : turnsTakenByTeam % playersOnTeam.length;

        return playersOnTeam[playerIndex] ?? this.turnSortedPlayers[0]!;
    }

    public isTeamTurn(player: Player) {
        return this.nextToPlay().team === player.team;
    }

    private onDataChanged(snapshot: GameDto) {
        // console.log('new snapshot:', snapshot);

        runInAction(() => {
            this._rawData = snapshot;

            this.state = this.determineState(snapshot);

            if (this.state === GameState.NoSuchGame) {
                return;
            }

            this.playerMap = Object.entries(snapshot?.players || {}).reduce((result, [id, data]) => {
                result[id] = new Player(id, data, id === this.deviceId);
                return result;
            }, {} as Record<string, Player>);

            this.turnSortedPlayers = this.sortPlayers(this.playerMap);
            this.teams = this.sortTeams(this.playerMap);

            this._db.fetchListWords(this.config.listWordSetId);

            if (![GameState.Spectate, GameState.Error].includes(this.state)) {
                if (!snapshot.game) {
                    return this._db.beginGame();
                }

                // End state conditions
                if (!this.isGameOver) {
                    if (this.isBoardFilled) {
                        return this._db.setEndState();
                    } else if (this.lastPlay?.word && this.wordEndsWithN(this.lastPlay.word.kana)) {
                        return this._db.setEndState();
                    }

                    if (this.hasDuplicatePlay) {
                        return this._db.setEndState();
                    }
                }
            }
        });
    }

    private sortPlayers(players: Record<string, Player>): Player[] {
        return Object.values(players).sort((a, b) => a.turnOrder - b.turnOrder);
    }

    private sortTeams(players: Record<string, Player>): Record<string, Player[]> {
        const groupedPlayers = Object.values(players).reduce((result, player) => {
            if (!result[player.team]) {
                result[player.team] = [];
            }

            result[player.team]!.push(player);
            return result;
        }, {} as Record<string, Player[]>);

        Object.values(groupedPlayers).forEach((teamPlayers) => {
            teamPlayers.sort((a, b) => a.turnOrder - b.turnOrder);
        });

        return groupedPlayers;
    }

    private get orderedTeams(): PlayerTeam[] {
        return this.turnSortedPlayers.reduce((order, player) => {
            if (player.team && !order.includes(player.team)) {
                order.push(player.team);
            }

            return order;
        }, [] as PlayerTeam[]);
    }

    private determineState(gameRoot: GameDto): GameState {
        const configKeys = Object.keys(gameRoot?.config || {});

        if (!gameRoot?.config || configKeys.length === 0) {
            return GameState.NoSuchGame;
        }

        if (gameRoot.game?.endState || Object.values(gameRoot.state || {}).includes('forfeit') || this.isBoardFilled) {
            return GameState.Ended;
        }

        const playersDto = gameRoot?.players || {};
        const playerKeys = Object.keys(playersDto);

        const expectedPlayerCount = gameRoot.config.expectedPlayers;

        if (expectedPlayerCount === playerKeys.length && !playersDto[this.deviceId]) {
            return GameState.Spectate;
        }

        if (expectedPlayerCount > 1 && this.turnCount === 0) {
            if (playerKeys.length === expectedPlayerCount) {
                const teamsReady = Object.values(playersDto).every((player) => player.ready === true);

                // If teams are ready, the game has already started
                if (teamsReady) {
                    return GameState.Multiplayer;
                }
            }

            return GameState.Lobby;
        }

        return GameState.Singleplayer;
    }

    public nomalizeToKana(rawInput: string) {
        const normalizedInput = this.normalizeInput(rawInput);
        if (!normalizedInput) {
            return '';
        }

        return normalizeToHiragana(normalizedInput);
    }

    public wordIsPlayable(kana: string): IsPlayableResult {
        let result: IsPlayableResult = {
            ok: false
        };

        const isInList = this._db.wordBelongsToList(kana);
        if (isInList) {
            if (this.turnCount === 0) {
                result.ok = true;
                return result;
            }

            const lastTurn = this.lastPlay;
            if (lastTurn?.word) {
                const lastWordEnding = getShiritoriEnding(lastTurn.word.kana);
                const thisWordBeginning = normalizeToHiragana(kana).at(0);

                if (lastWordEnding !== thisWordBeginning) {
                    result.messageKey = 'syllableMismatch';
                } else {
                    result.ok = true;
                    return result;
                }
            } else {
                result.messageKey = 'missingLastWord';
            }
        } else {
            result.messageKey = 'wordNotInList';
        }

        return result;
    }

    public wordEndsWithN(kana: string): boolean {
        return getShiritoriEnding(kana) === 'ん';
    }

    public async searchWord(kana: string) {
        if (!kana) {
            return [];
        }

        const homonymGroup = await this._db.getWordGroup(kana);

        const matches = homonymGroup ? Object.values(homonymGroup) : [];
        this.sortWordMatches(matches);

        return matches;
    }

    public isKanjiInput(rawInput: string) {
        return /\p{Script=Han}/u.test(rawInput.normalize('NFKC'));
    }

    public async searchKanji(rawInput: string) {
        const writtenForm = rawInput.normalize('NFKC').trim();
        if (!writtenForm || !this.isKanjiInput(writtenForm)) {
            return [];
        }

        const lookup = await this._db.getKanjiLookup(writtenForm);
        if (!lookup) {
            return [];
        }

        const matches = await Promise.all(Object.entries(lookup).map(async ([kana, imageId]) => {
            const wordGroup = await this._db.getWordGroup(kana);
            return wordGroup
                ? Object.values(wordGroup).find((word) => word.imageId === imageId)
                : undefined;
        }));

        const exactMatches = matches.filter((word): word is ShiritoriWordDto => !!word);
        this.sortWordMatches(exactMatches);
        return exactMatches;
    }

    public async playWord(word: ShiritoriWordDto) {
        if (!this.isGameOver && this._db.wordBelongsToList(word.kana)) {
            const cellIndex = this.turnToSnakeCell(this.turnCount, this.config.boardSize);
            await this._db.playWord(word, this.devicePlayer, cellIndex);
        }
    }

    public async markTurnSeen() {
        if (this.state === GameState.Ended || !this.isTimerActive || !this.isDeviceTurn) {
            return;
        }

        const durationSeconds = this.turnTimerSeconds;
        const timer = this.timerState;
        const turnIndex = this.currentTurnIndex;

        if (timer && timer.turnIndex === turnIndex) {
            return;
        }

        await this._db.markTurnSeen(turnIndex, this.nextToPlay(), durationSeconds);
    }

    public async expireTurnIfNeeded() {
        const timer = this.timerState;
        if (!timer || this.state === GameState.Ended) {
            return;
        }

        const remainingSeconds = this.currentTurnRemainingSeconds();
        if (remainingSeconds === undefined || remainingSeconds > 0) {
            return;
        }

        await this._db.expireTurnIfElapsed(timer.turnIndex);
    }

    public async leaveGame() {
        await this._db.leaveGame();
    }

    turnToSnakeCell(turnIndex: number, n: number): number {
        const row = Math.floor(turnIndex / n);
        const colInRow = turnIndex % n;
        const isEvenRow = row % 2 === 0;

        if (isEvenRow) {
            // left -> right
            return row * n + colInRow;
        } else {
            // right -> left
            const col = n - 1 - colInRow;
            return row * n + col;
        }
    }

    public async getImageSrc(imageId: string): Promise<string> {
        const imageSet = this.config.pictureSetId;
        const url = await this._db.getImageUrl(imageSet, imageId);
        return url;
    }

    private normalizeInput(rawInput: string): string {
        return rawInput
            .normalize('NFKC')
            .trim()
            .toLowerCase()
            // a-z (English letters) OR \u3040-\u309f (Hiragana) OR \u30a0-\u30ff (Katakana) OR ー (Chōon: long vowel), but preserve apostrophes so Wanakana can distinguish n + vowel/y
            .replace(/[^a-z'\u3040-\u309f\u30a0-\u30ffー]/g, '');
    }

    private sortWordMatches(matches: ShiritoriWordDto[]) {
        matches.sort((a, b) => {
            const aRank = a.rank && a.rank > 0 ? a.rank : Number.MAX_SAFE_INTEGER;
            const bRank = b.rank && b.rank > 0 ? b.rank : Number.MAX_SAFE_INTEGER;
            return aRank - bRank;
        });
    }

}
